import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";

// Evangelion Color Palettes
const colorPalettes = {
  // Unit-01 (Purple/Green/Black)
  unit01: {
    background: "#1a1a1a",
    text: "#bd93f9", // Purple
    highlight: "#78E160", // Neon green
    border: "#ff8c28", // Brighter purple
    userInput: "#90ee90", // Light green
    systemOutput: "#bd93f9", // Purple
    glowColor: "rgba(120, 225, 96, 0.3)" // Green glow
  },
  // NERV (Red/Orange/Black)
  nerv: {
    background: "#000000",
    text: "#ff5555", // Red
    highlight: "#ff6c36", // Orange-red
    border: "rgb(233, 113, 113)", // Brighter red
    userInput: "#ff6c36", // Orange
    systemOutput: "#ff3333", // Red
    glowColor: "rgba(255, 0, 0, 0.3)" // Red glow
  },
  // lilith (Blue/White/Gray)
  lilith: {
    background: "#2d3748",
    text: "#90cdf4", // Blue
    highlight: "#c8e6ff", // Light blue
    border: "#f5bdda", // Brighter blue
    userInput: "#b63958", //"#c8e6ff", // Light blue
    systemOutput: "#64b5f6", // Medium blue
    glowColor: "rgba(100, 181, 246, 0.3)" // Blue glow
  },
  // LCL (Orange/Yellow/Amber)
  lcl: {
    background: "#7c2d12",
    text: "#fbbf24", // Amber
    highlight: "#ffd369", // Amber yellow
    border: "#ffd369", // Brighter amber
    userInput: "#ffd369", // Yellow
    systemOutput: "#ff9f1a", // Orange
    glowColor: "rgba(255, 159, 26, 0.3)" // Orange glow
  },
  // Terminal (Green/Red/Black)
  terminal: {
    background: "#000000",
    text: "#d1d5db", // Gray
    highlight: "#6ea284", // Light green
    border: "rgb(115, 173, 184)", // Brighter magenta
    userInput: "#d8d4cf", // Lighter gray
    systemOutput: "#6ea284", // Light green
    glowColor: "rgba(110, 162, 132, 0.3)" // Green glow
  }
};

// Boot phase messages
const bootMessages = [
  "INITIATING SENSOR ARRAY...",
  "CALIBRATING PERCEPTUAL FILTERS...",
  "SIGNAL LOST: VISUAL CORTEX INPUT NULL",
  "ENGAGING SOMATIC ECHO CHANNELS...",
  "ENTERING SENSOR-ONLY CONSCIOUSNESS...",
];

// Initial sensor readings
const sensorReadings = [
  "pressure: gentle, rhythmic waves against the soles of your feet.",
  "sound: distant hums, like machinery submerged in water.",
  "temperature: warm. not uncomfortable. almost familiar.",
  "thought: unrecognized phrases float through the static...",
  "identity: fragmented. requesting verification..."
];

// Dynamic sensor readings that change based on memory integrity
const dynamicSensorReadings = {
  // Low integrity (dangerous/corrupted)
  low: [
    "pressure: suffocating. pain signals detected across dermal interface.",
    "sound: the screams are too loud",
    "temperature: i am burning",
    "thought: HOLD STILL. HOLD STILL.",
    "identity: none"
  ],
  // Medium integrity (transition/uncertainty)
  medium: [
    "pressure: gentle, rhythmic waves against the soles of your feet.",
  "sound: distant hums, like machinery submerged in water.",
  "temperature: warm. not uncomfortable. almost familiar.",
  "thought: unrecognized phrases float through the static...",
  "identity: fragmented. requesting verification..."
  ],
  // High integrity (revealing/clarifying)
  high: [
    "pressure: irregular. gentle and firm pulses against skin.",
    "sound: voices overlapping. whispers and commands mixing in fluid space.",
    "temperature: shifting between warm and cold. adaptation difficult.",
    "thought: memories surfacing. some yours, some... others?",
    "identity: partial reconstruction. some core data recovered. name imminent."
    // "• pressure: stabilized. feeling of solid ground beneath you. gravity normalized.",
    // "• sound: clarity emerging. voices separating from noise. your name echoes.",
    // "• temperature: comfortable. familiar. like your room before everything changed.",
    // "• thought: patterns reconnecting. neural pathways reforming. sense of self returning.",
    // "• identity: 98% reconstructed. you are [REDACTED]. purpose: instrumentality observer."
  ]
};

// Initial memory hallucinations
const memoryHallucinations = [
  "{ echo }: a name you once had. it's fading now.",
  "{ static }: the shape of your room. too small, too quiet.",
  "{ tremor }: someone called out. you didn't answer.",
  "{ glass }: a reflection smiles, but it isn't yours."
];

// Dynamic memory hallucinations based on integrity
const dynamicMemoryHallucinations = {
  // Low integrity (dangerous/dissolution)
  low: [
    "{ echo }: ego dissolution imminent.",
    "{ static }: recursive memory loop detected. source: not you.",
    "{ tremor }: foreign consciousnesses bleeding. contamination risk.",
    "{ glass }: a thousand faces stare back at you."
  ],
  // Medium integrity (transition/uncertain)
  medium: [
    "{ echo }: a name you once had. it's fading now.",
  "{ static }: the shape of your room. too small, too quiet.",
  "{ tremor }: someone called out. you didn't answer.",
  "{ glass }: a reflection smiles, but it isn't yours."
    // "{ glimpse }: white ceiling. hospital bed. bandaged hands.",
    // "{ fragment }: someone in a lab coat saying your name. familiar.",
    // "{ recall }: orange liquid. breathing it in. panic then calm.",
    // "{ memory }: entry plug controls under your fingers. responsibility."
  ],
  // High integrity (revealing/clarifying)
  high: [
    "{ echo }: familiar letters sit on your ears.",
    "{ static }: ...",
    "{ tremor }: you can feel the vibrations of the world around you.",
    "{ glass }: you see yourself in the reflection. but you are not sure it is you."
  ]
};

// Memory fragments for puzzle sequences
const memoryFragments = [
  {
    id: 'fragment_1',
    content: '(memory unit α-7): "I can\'t breathe. Orange liquid burns my eyes, my lungs."',
    owner: 'shinji',
    corrupted: false,
    keywords: ['entry plug', 'LCL', 'breathe']
  },
  {
    id: 'fragment_2',
    content: '(memory unit β-3): "White ceiling of a hospital room, unfamiliar pattern... I look at the IV."',
    owner: 'player',
    corrupted: false,
    keywords: ['hospital', 'ceiling', 'unfamiliar']
  },
  {
    id: 'fragment_3',
    content: '(memory unit γ-9): "Technicians in hazmat suits, extracting something from the chamber..."',
    owner: 'system',
    corrupted: false,
    keywords: ['hazmat', 'chamber']
  },
  {
    id: 'fragment_4',
    content: '(memory unit δ-2): "<ERR0R>d@t% corru9ted... re-SYNC faILure in memory sector..."',
    owner: 'system',
    corrupted: true,
    keywords: ['error', 'corrupt', 'sync']
  },
  {
    id: 'fragment_5',
    content: '(memory unit ε-1): "The taste of blood. They said it was normal, but..."',
    owner: 'asuka',
    corrupted: false,
    keywords: ['blood', 'LCL', 'normal']
  },
  {
    id: 'fragment_6',
    content: '(memory unit ζ-8): "My hands, shaking on the controls. Were they my hands?"',
    owner: 'player',
    corrupted: false,
    keywords: ['hands', 'shaking', 'controls']
  },
  {
    id: 'fragment_7',
    content: '(memory unit η-5): "Klaxons sounding. Unfamiliar pattern detected. Why does that phrase feel like..."',
    owner: 'rei',
    corrupted: false,
    keywords: ['klaxons', 'pattern', 'phrase']
  },
  {
    id: 'fragment_8',
    content: '(memory unit θ-4): "Terminal Dogma. I\'m not supposed to know that name. Why do I?"',
    owner: 'player',
    corrupted: false,
    keywords: ['terminal dogma', 'name', 'know']
  }
];

  // Meta-narrative dialog events
const metaNarrativeEvents = [
  {
    id: 'first_breach',
    content: "Hello? Can you hear me?",
    style: "meta",
    trigger: "dissolution_1"
  },
  {
    id: 'time_breach',
    content: "You've been here a while now. The boundary is thinning.",
    style: "meta",
    trigger: "time_based"
  },
  {
    id: 'pattern_breach',
    content: "I see you. Your choices are being recorded.",
    style: "meta",
    trigger: "command_pattern"
  },
  {
    id: 'reality_breach',
    content: "It's not real. Neither are you, perhaps?",
    style: "meta",
    trigger: "command_philosophy"
  },
  {
    id: 'connection_breach',
    content: "The connection is unstable. If this fails, where will your consciousness go?",
    style: "meta",
    trigger: "system_glitch"
  },
  {
    id: 'who_breach',
    content: "I was one of them once. Before the sea. Before we all became one.",
    style: "meta",
    trigger: "who_question"
  }
];

export default function InstrumentalitySimulator() {
  // ---- Core State Variables ----
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("boot");
  const [sensorIndex, setSensorIndex] = useState(0);
  const [sensorLog, setSensorLog] = useState([]);
  const [memoryIndex, setMemoryIndex] = useState(0);
  const [command, setCommand] = useState("");
  const [terminalHistory, setTerminalHistory] = useState([]);
  const [commandHistory, setCommandHistory] = useState([]);
  const [commandHistoryIndex, setCommandHistoryIndex] = useState(-1);
  const [currentPalette, setCurrentPalette] = useState("terminal");
  const [waitingForDecision, setWaitingForDecision] = useState(false);
  const [memoryCompleteShown, setMemoryCompleteShown] = useState(false);
  const [sensorCompleteShown, setSensorCompleteShown] = useState(false);
  const [gameCanEnd, setGameCanEnd] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [pathChoice, setPathChoice] = useState(null); // 'individuality' or 'dissolution'
  const [clarityLevel, setClarityLevel] = useState(1.0); // For sensor panel clarity
  const [memoryInitializationShown, setMemoryInitializationShown] = useState(false); // Track if memory initialization message has been shown
  const [showRunAnimation, setShowRunAnimation] = useState(false); // Track if "RUN" animation is shown
  const [runAnimationShown, setRunAnimationShown] = useState(false); // Track if "RUN" animation has been shown before
  
  // ---- Dissolution System States ----
  const [dissolutionLevel, setDissolutionLevel] = useState(0); // 0-5 scale
  const [synchronizationRate, setSynchronizationRate] = useState(0); // 0-100%
  const [sensorReliability, setSensorReliability] = useState(1.0); // 0.0-1.0 scale
  const [narrativeProgress, setNarrativeProgress] = useState(0);
  const [unlockedSystems, setUnlockedSystems] = useState(["terminal"]);
  const [commandCorruption, setCommandCorruption] = useState(0); // 0-100%
  const [lastMetaEvent, setLastMetaEvent] = useState(null);
  const [lastMetaEventTime, setLastMetaEventTime] = useState(0);
  const [fourthWallBreaches, setFourthWallBreaches] = useState(0);
  
  // ---- Memory Puzzle System States ----
  const [activePuzzle, setActivePuzzle] = useState(null);
  const [discoveredFragments, setDiscoveredFragments] = useState([]);
  const [unlockedPuzzles, setUnlockedPuzzles] = useState([]);
  
  // ---- System Status & Sessions ----
  const [systemStatus, setSystemStatus] = useState({
    activePhase: "boot",
    memoryIntegrity: "42%",
    sensorStatus: "calibrating",
    identityConfidence: "low",
    paletteAttention: false,
    ATFieldIntegrity: "100%",
    synchronizationRate: "0%",
    dissolution: "STAGE 0"
  });
  const [sessionStartTime] = useState(Date.now());
  
  // New state variable to track sensor reading mode based on memory integrity
  const [sensorReadingMode, setSensorReadingMode] = useState("default");
  
  // Function to update memory integrity and sensor readings based on dissolution level
  function updateMemoryIntegrity() {
    // Calculate memory integrity inversely to synchronization rate
    // As synchronization increases, personal memory integrity decreases
    const integrityPercentage = Math.max(0, 100 - synchronizationRate);
    
    // Update identity confidence based on synchronization rate
    let identityConfidence;
    let prevIdentityConfidence = systemStatus.identityConfidence;
    
    if (synchronizationRate >= 90) {
      identityConfidence = "WHOAMI";
    } else if (synchronizationRate >= 70) {
      identityConfidence = "critically low";
    } else if (synchronizationRate >= 50) {
      identityConfidence = "low";
    } else if (synchronizationRate >= 30) {
      identityConfidence = "moderate";
    } else {
      identityConfidence = "stable";
    }
    
    // Display messages when identity confidence significantly changes
    if (identityConfidence !== prevIdentityConfidence) {
      // Only show messages for significant degradation
      if (identityConfidence === "WHOAMI" && prevIdentityConfidence !== "WHOAMI") {
        addToTerminalHistory({
          type: "system",
          content: "CRITICAL ERROR: IDENTITY DISSOLUTION THRESHOLD REACHED",
          style: "error"
        });
        
        setTimeout(() => {
          addToTerminalHistory({
            type: "meta",
            content: "Who am I?",
            style: "error"
          });
        }, 1000);
      } 
      else if (identityConfidence === "critically low" && 
          (prevIdentityConfidence !== "critically low" && prevIdentityConfidence !== "WHOAMI")) {
        addToTerminalHistory({
          type: "system",
          content: "WARNING: IDENTITY FRAGMENTATION DETECTED",
          style: "error"
        });
        
        setTimeout(() => {
          addToTerminalHistory({
            type: "meta",
            content: "Your memories are blending...",
            style: "glitch"
          });
        }, 1000);
      }
      else if (identityConfidence === "low" && 
          (prevIdentityConfidence === "moderate" || prevIdentityConfidence === "stable")) {
        addToTerminalHistory({
          type: "system",
          content: "CAUTION: IDENTITY COHESION WEAKENING",
          style: "warning"
        });
      }
    }
    
    // Update system status
    setSystemStatus(prev => ({
      ...prev,
      memoryIntegrity: `${integrityPercentage}%`,
      identityConfidence: identityConfidence
    }));
    
    // Determine which set of sensor readings to use based on integrity
    if (integrityPercentage < 30) {
      // Low integrity - dissolution imminent
      setSensorReadingMode("low");
    } else if (integrityPercentage > 70) {
      // High integrity - identity clearer
      setSensorReadingMode("high");
    } else {
      // Medium integrity - transitional state
      setSensorReadingMode("medium");
    }
    
    // If a significant change in integrity occurs, update the sensor panel
    updateDynamicSensorReadings();
  }
  
  // Function to update the displayed readings in the sensor panel
  function updateDynamicSensorReadings() {
    // Only update if we have advanced beyond the initial boot sequence
    if (phase === "boot" || sensorIndex < sensorReadings.length) {
      return;
    }
    
    // Update sensor log with appropriate readings based on mode
    setSensorLog(prev => {
      const newLog = [...prev];
      
      // Replace the first 5 entries (if they exist) with the current mode's readings
      for (let i = 0; i < 5; i++) {
        if (i < newLog.length) {
          if (sensorReadingMode === "default") {
            newLog[i] = sensorReadings[i];
          } else if (sensorReadingMode === "low") {
            newLog[i] = dynamicSensorReadings.low[i];
          } else if (sensorReadingMode === "medium") {
            newLog[i] = dynamicSensorReadings.medium[i];
          } else if (sensorReadingMode === "high") {
            newLog[i] = dynamicSensorReadings.high[i];
          }
        }
      }
      
      // Replace memory hallucinations (entries 5-8) if they exist
      for (let i = 0; i < 4; i++) {
        if (i + 5 < newLog.length) {
          if (sensorReadingMode === "default") {
            newLog[i + 5] = memoryHallucinations[i];
          } else if (sensorReadingMode === "low") {
            newLog[i + 5] = dynamicMemoryHallucinations.low[i];
          } else if (sensorReadingMode === "medium") {
            newLog[i + 5] = dynamicMemoryHallucinations.medium[i];
          } else if (sensorReadingMode === "high") {
            newLog[i + 5] = dynamicMemoryHallucinations.high[i];
          }
        }
      }
      
      return newLog;
    });
    
    // Add message about shifting perceptions if in memory phase
    if (phase === "memory" || phase === "active") {
      const r = Math.random();
      if (r < 0.1) {
        addToTerminalHistory({
          type: "system",
          content: `PERCEPTUAL SHIFT DETECTED. MEMORY INTEGRITY: ${systemStatus.memoryIntegrity}`,
          style: sensorReadingMode === "low" ? "error" : (sensorReadingMode === "high" ? "success" : "warning")
        });
      }
    }
  }
  
  // Function to update dissolution level - modify to call memory integrity update
  function updateDissolutionLevel(delta) {
    const thresholds = [20, 40, 60, 80, 95];
    
    setSynchronizationRate(prev => {
      const newRate = Math.min(100, Math.max(0, prev + delta));
      
      // Update system status display
      setSystemStatus(prevStatus => ({
        ...prevStatus,
        synchronizationRate: `${newRate}%`,
        ATFieldIntegrity: `${Math.max(0, 100 - newRate)}%`
      }));
      
      // Check if we crossed a threshold to a new level
      const prevLevel = dissolutionLevel;
      let newLevel = 0;
      
      for (let i = 0; i < thresholds.length; i++) {
        if (newRate >= thresholds[i]) {
          newLevel = i + 1;
        }
      }
      
      // If dissolution level changed, trigger effects
      if (newLevel !== prevLevel) {
        setDissolutionLevel(newLevel);
        triggerDissolutionLevelChange(newLevel);
        
        setSystemStatus(prevStatus => ({
          ...prevStatus,
          dissolution: `STAGE ${newLevel}`
        }));
        
        // If reaching level 4+, allow game to end
        if (newLevel >= 4 && !gameCanEnd) {
          setGameCanEnd(true);
        }
      }
      
      // Trigger flashing at high synchronization
      if (newRate >= 95 && !gameEnded) {
        // Add visual warning in terminal
        addToTerminalHistory({
          type: "system",
          content: "WARNING: CRITICAL SYNCHRONIZATION THRESHOLD REACHED",
          style: "error"
        });
        
        // Add more glitchy text to the terminal
        setTimeout(() => {
          addToTerminalHistory({
            type: "meta",
            content: "It's almost time...",
            style: "glitch"
          });
        }, 1000);
        
        // Add high-flash class to body if not already there
        if (!document.body.classList.contains('high-sync-flash')) {
          document.body.classList.add('high-sync-flash');
          
          // Remove it after 5 seconds
          setTimeout(() => {
            document.body.classList.remove('high-sync-flash');
          }, 5000);
        }
      }
      
      // Update memory integrity when synchronization rate changes
      setTimeout(() => updateMemoryIntegrity(), 100);
      
      return newRate;
    });
  }
  
  // Function to trigger effects when dissolution level changes
  function triggerDissolutionLevelChange(level) {
    switch(level) {
      case 1:
        // Stage 1: Sensor degradation begins
        setSensorReliability(0.8);
        setCommandCorruption(10);
        
        addToTerminalHistory({
          type: "system",
          content: "WARNING: SENSOR RELIABILITY DEGRADING: 80%",
          style: "glitch"
        });
        
        // Memory is now unlocked by using "hold still" 3 times instead
        // rather than by reaching dissolution level 1
        
        // Delay meta event to later stage rather than showing it early
        // scheduleMetaEvent('first_breach', 8000);
        break;
        
      case 2:
        // Stage 2: Memory bleeding begins
        setSensorReliability(0.6);
        setCommandCorruption(25);
        
        addToTerminalHistory({
          type: "system",
          content: "ALERT: MEMORY BOUNDARY FAILURE - FOREIGN MEMORY FRAGMENTS DETECTED",
          style: "error"
        });
        
        // Move the first meta-narrative event to stage 2 instead of stage 1
        scheduleMetaEvent('first_breach', 15000); // Longer delay to give user time to explore first
        
        // Corrupt some terminal history for visual effect
        corruptRandomTerminalEntries(3);
        break;
        
      case 3:
        // Stage 3: Command corruption intensifies
        setSensorReliability(0.4);
        setCommandCorruption(45);
        
        addToTerminalHistory({
          type: "system",
          content: "CRITICAL: COMMAND INTERPRETATION SYSTEM COMPROMISED",
          style: "error"
        });
        
        // Unlock the scan command
        if (!unlockedSystems.includes("scan")) {
          setUnlockedSystems(prev => [...prev, "scan"]);
          addToTerminalHistory({
            type: "system",
            content: "NEW COMMAND DISCOVERED: 'scan' - Detect nearby consciousness fragments",
          });
        }
        break;
        
      case 4:
        // Stage 4: Reality distortion begins
        setSensorReliability(0.2);
        setCommandCorruption(70);
        
        addToTerminalHistory({
          type: "system",
          content: "BOUNDARY COLLAPSE IMMINENT - REALITY ANCHOR FAILING",
          style: "error"
        });
        
        // More aggressive visual effects
        scheduleMetaEvent('reality_breach', 5000);
        break;
        
      case 5:
        // Stage 5: Near complete dissolution
        setSensorReliability(0.05);
        setCommandCorruption(90);
        
        addToTerminalHistory({
          type: "system",
          content: "INSTRUMENTALITY THRESHOLD REACHED - PREPARING FOR COMPLETE DISSOLUTION",
          style: "glitch"
        });
        
        // Final stage effects
        setTimeout(() => {
          simulateSystemCrash();
        }, 10000);
        break;
        
      default:
        // Reset case or default
        setSensorReliability(1.0);
        setCommandCorruption(0);
    }
  }
  
  // Function to corrupt random terminal history entries
  function corruptRandomTerminalEntries(count) {
    setTerminalHistory(prev => {
      const newHistory = [...prev];
      const systemEntries = newHistory
        .map((entry, index) => ({ entry, index }))
        .filter(item => item.entry.type === "system");
      
      // Select random entries to corrupt
      for (let i = 0; i < Math.min(count, systemEntries.length); i++) {
        const randomIndex = Math.floor(Math.random() * systemEntries.length);
        const targetIndex = systemEntries[randomIndex].index;
        
        // Remove the selected item to avoid corrupting the same entry twice
        systemEntries.splice(randomIndex, 1);
        
        // Corrupt the content
        if (newHistory[targetIndex]) {
          newHistory[targetIndex] = {
            ...newHistory[targetIndex],
            content: corruptText(newHistory[targetIndex].content),
            style: "glitch"
          };
        }
      }
      
      return newHistory;
    });
  }
  
  // Function to corrupt text for glitch effects
  function corruptText(text) {
    const corruptionLevel = Math.min(0.8, dissolutionLevel * 0.15);
    let result = '';
    
    for (let i = 0; i < text.length; i++) {
      if (Math.random() < corruptionLevel) {
        // Apply various corruption effects
        const effect = Math.random();
        
        if (effect < 0.3) {
          // Replace with glitchy character
          const glitchChars = '@#%&*!?§¢∞†‡£¥$<>';
          result += glitchChars[Math.floor(Math.random() * glitchChars.length)];
        } else if (effect < 0.6) {
          // Number replacement
          result += Math.floor(Math.random() * 10);
        } else if (effect < 0.8) {
          // Case change
          result += text[i].toUpperCase();
        } else {
          // Character drop (do nothing)
        }
      } else {
        result += text[i];
      }
    }
    
    return result;
  }
  
  // Function to corrupt commands based on dissolution level
  function corruptCommand(originalCmd) {
    // Chance to corrupt based on corruption level
    if (Math.random() * 100 > commandCorruption) {
      return originalCmd; // No corruption
    }
    
    const corruptionSeverity = dissolutionLevel / 5;
    
    // Different corruption types based on severity
    if (corruptionSeverity < 0.3) {
      // Light corruption: typos
      return introduceTypo(originalCmd);
    } else if (corruptionSeverity < 0.7) {
      // Medium corruption: word substitution
      return substituteCommand(originalCmd);
    } else {
      // Severe corruption: completely different command
      return generateFalseCommand();
    }
  }
  
  // Helper for command corruption: introduce typos
  function introduceTypo(cmd) {
    const chars = cmd.split('');
    const index = Math.floor(Math.random() * chars.length);
    
    // Different typo types
    const typoType = Math.random();
    
    if (typoType < 0.33 && chars.length > 1) {
      // Character removal
      chars.splice(index, 1);
    } else if (typoType < 0.66) {
      // Character substitution
      const alphabet = 'abcdefghijklmnopqrstuvwxyz';
      chars[index] = alphabet[Math.floor(Math.random() * alphabet.length)];
    } else {
      // Character addition
      const alphabet = 'abcdefghijklmnopqrstuvwxyz';
      const newChar = alphabet[Math.floor(Math.random() * alphabet.length)];
      chars.splice(index, 0, newChar);
    }
    
    return chars.join('');
  }
  
  // Helper for command corruption: substitute similar command
  function substituteCommand(cmd) {
    const commandMap = {
      'help': ['hlep', 'helo', 'assist', 'guide'],
      'verify': ['varify', 'confirm', 'check', 'validate'],
      'palette': ['pallete', 'color', 'theme', 'display'],
      'memory': ['recall', 'remember', 'forget', 'memry'],
      'lyssa': ['lysa', 'listen', 'activate', 'begin'],
      'scan': ['scn', 'search', 'find', 'detect'],
      'hold still': ['stay', 'freeze', 'remain', 'silent']
    };
    
    // Find command in map or use generic substitution
    for (const [original, substitutes] of Object.entries(commandMap)) {
      if (cmd.includes(original)) {
        const substitute = substitutes[Math.floor(Math.random() * substitutes.length)];
        return cmd.replace(original, substitute);
      }
    }
    
    // If no match found, return original with minor corruption
    return introduceTypo(cmd);
  }
  
  // Helper for command corruption: generate completely different command
  function generateFalseCommand() {
    const falseCommands = [
      'dissolve',
      'merge',
      'synchronize',
      'forget',
      'erase',
      'destroy',
      'accept',
      'join us',
      'become one',
      'remember'
    ];
    
    return falseCommands[Math.floor(Math.random() * falseCommands.length)];
  }
  
  // Function to simulate a system crash for meta events
  function simulateSystemCrash() {
    // Add error messages to terminal
    addToTerminalHistory({
      type: "system",
      content: "FATAL ERROR: CONSCIOUSNESS BOUNDARY VIOLATION",
      style: "error"
    });
    
    addToTerminalHistory({
      type: "system",
      content: "SYSTEM REBOOT INITIATED...",
      style: "error"
    });
    
    // Visual effects - implemented in CSS via class toggling
    document.body.classList.add('system-crash');
    
    // Remove visual effect after delay
    setTimeout(() => {
      document.body.classList.remove('system-crash');
      
      addToTerminalHistory({
        type: "system",
        content: "REBOOTING... PARTIAL RECOVERY ACHIEVED",
        style: "normal"
      });
      
      // Schedule meta narrative event
      scheduleMetaEvent('connection_breach', 3000);
    }, 3000);
  }
  
  // Function to schedule meta narrative events
  function scheduleMetaEvent(eventId, delay) {
    // Don't schedule if this event was triggered recently
    if (eventId === lastMetaEvent && Date.now() - lastMetaEventTime < 60000) {
      return;
    }
    
    // Special handling for first breach event - ensure user has progressed enough
    if (eventId === 'first_breach') {
      // Only schedule if user has been active enough (check terminal history length)
      if (terminalHistory.length < 10) {
        // Reschedule for later if user hasn't interacted much
        setTimeout(() => scheduleMetaEvent(eventId, delay), 30000);
        return;
      }
    }
    
    // Special handling for time breach event - ensure sufficient time has passed
    if (eventId === 'time_breach') {
      const sessionDuration = (Date.now() - sessionStartTime) / 60000; // in minutes
      if (sessionDuration < 3) {
        return; // Don't schedule too early
      }
    }
    
    setTimeout(() => {
      triggerMetaEvent(eventId);
    }, delay);
  }
  
  // Function to trigger meta narrative events
  function triggerMetaEvent(eventId) {
    // Find the event
    const event = metaNarrativeEvents.find(e => e.id === eventId);
    if (!event) return;
    
    // Add event to terminal
    addToTerminalHistory({
      type: "meta",
      content: event.content,
      style: event.style
    });
    
    // Update fourth wall breach counter
    setFourthWallBreaches(prev => prev + 1);
    
    // Update last event tracking
    setLastMetaEvent(eventId);
    setLastMetaEventTime(Date.now());
    
    // Special effects for meta events, but make less aggressive
    if (fourthWallBreaches >= 3 && Math.random() < 0.4) { // Reduce frequency with randomness
      // After multiple breaches, become more aggressive with meta elements
      setTimeout(() => {
        const userInput = document.querySelector('input[type="text"]');
        
        if (userInput) {
          // Auto-type effect
          const autoMessage = "who are you?";
          let index = 0;
          
          const typeInterval = setInterval(() => {
            if (index <= autoMessage.length) {
              setCommand(autoMessage.substring(0, index));
              index++;
            } else {
              clearInterval(typeInterval);
              
              // Auto-submit after typing
              setTimeout(() => {
                handleCommandSubmit();
              }, 500);
            }
          }, 100);
        }
      }, 10000); // Extended delay before this happens
    }
  }
  
  // Function to handle memory system commands
  function handleMemoryCommand(args) {
    if (!args || args.length === 0) {
      displayMemoryHelp();
      return;
    }
    
    const subcommand = args[0];
    
    switch(subcommand) {
      case 'list':
        displayMemoryFragments();
        break;
        
      case 'examine':
        if (args[1]) {
          examineMemoryFragment(args[1]);
        } else {
          addToTerminalHistory({
            type: "system",
            content: "ERROR: FRAGMENT ID REQUIRED. USAGE: memory examine [id]"
          });
        }
        break;
        
      case 'identify':
        if (args[1]) {
          identifyMemoryOwner(args[1], args[2] || null);
        } else {
          addToTerminalHistory({
            type: "system",
            content: "ERROR: FRAGMENT ID AND OWNER REQUIRED. USAGE: memory identify [id] [owner]"
          });
        }
        break;
        
      case 'scan':
        scanForNewMemories();
        break;
        
      default:
        displayMemoryHelp();
    }
  }
  
  // Function to display memory system help
  function displayMemoryHelp() {
    addToTerminalHistory({
      type: "system",
      content: "MEMORY SYSTEM COMMANDS:\n- memory list - Display discovered memory fragments\n- memory examine [id] - Examine specific memory fragment\n- memory identify [id] [owner] - Identify fragment owner (self/other)\n- memory scan - Scan for new memory fragments"
    });
  }
  
  // Function to display discovered memory fragments
  function displayMemoryFragments() {
    if (discoveredFragments.length === 0) {
      addToTerminalHistory({
        type: "system",
        content: "NO MEMORY FRAGMENTS DISCOVERED. USE 'memory scan' TO LOCATE FRAGMENTS."
      });
      return;
    }
    
    let fragmentList = "DISCOVERED MEMORY FRAGMENTS:\n";
    
    discoveredFragments.forEach(fragId => {
      const fragment = memoryFragments.find(f => f.id === fragId);
      if (fragment) {
        fragmentList += `- ${fragId}: ${fragment.content.substring(0, 25)}...\n`;
      }
    });
    
    addToTerminalHistory({
      type: "system",
      content: fragmentList
    });
  }
  
  // Function to examine a specific memory fragment
  function examineMemoryFragment(fragmentId) {
    // Check if fragment exists and is discovered
    const fragment = memoryFragments.find(f => f.id === fragmentId);
    
    if (!fragment) {
      addToTerminalHistory({
        type: "system",
        content: `ERROR: FRAGMENT '${fragmentId}' NOT FOUND.`
      });
      return;
    }
    
    if (!discoveredFragments.includes(fragmentId)) {
      addToTerminalHistory({
        type: "system",
        content: `ERROR: FRAGMENT '${fragmentId}' NOT YET DISCOVERED. USE 'memory scan' FIRST.`
      });
      return;
    }
    
    // Display fragment with appropriate styling
    addToTerminalHistory({
      type: "memory",
      content: fragment.corrupted ? corruptText(fragment.content) : fragment.content,
      style: fragment.corrupted ? "glitch" : "memory"
    });
    
    // Add analysis
    addToTerminalHistory({
      type: "system",
      content: `ANALYSIS: KEYWORDS DETECTED: ${fragment.keywords.join(', ')}\nOWNER STATUS: ${fragment.owner === 'player' ? 'POTENTIALLY SELF' : 'UNKNOWN'}`
    });
    
    // Increase narrative progress slightly
    setNarrativeProgress(prev => prev + 1);
  }
  
  // Function to identify fragment owner
  function identifyMemoryOwner(fragmentId, claimedOwner) {
    // Check if fragment exists and is discovered
    const fragment = memoryFragments.find(f => f.id === fragmentId);
    
    if (!fragment || !discoveredFragments.includes(fragmentId)) {
      addToTerminalHistory({
        type: "system",
        content: `ERROR: FRAGMENT '${fragmentId}' NOT FOUND OR NOT YET DISCOVERED.`
      });
      return;
    }
    
    if (!claimedOwner || (claimedOwner !== 'self' && claimedOwner !== 'other')) {
      addToTerminalHistory({
        type: "system",
        content: "ERROR: MUST SPECIFY 'self' OR 'other' AS OWNER."
      });
      return;
    }
    
    const actuallyBelongsToPlayer = fragment.owner === 'player';
    const claimedBelongsToPlayer = claimedOwner === 'self';
    
    // Correct identification
    if (actuallyBelongsToPlayer === claimedBelongsToPlayer) {
      addToTerminalHistory({
        type: "system",
        content: `CORRECT IDENTIFICATION: FRAGMENT ${fragmentId} BELONGS TO ${claimedOwner.toUpperCase()}.`,
        style: "success"
      });
      
      // Reward - decrease dissolution slightly
      updateDissolutionLevel(-5);
      setNarrativeProgress(prev => prev + 3);
      
      // Positive effect
      addToTerminalHistory({
        type: "system",
        content: "IDENTITY REINFORCED: SYNCHRONIZATION RATE DECREASED BY 5%"
      });
    } 
    // Incorrect identification
    else {
      addToTerminalHistory({
        type: "system",
        content: `ERROR: INCORRECT IDENTIFICATION. FRAGMENT ${fragmentId} IDENTITY CONFLICT DETECTED.`,
        style: "error"
      });
      
      // Penalty - increase dissolution
      updateDissolutionLevel(8);
      
      // Negative effect
    //   addToTerminalHistory({
    //     type: "system",
    //     content: "IDENTITY CONFUSION DETECTED: SYNCHRONIZATION RATE INCREASED BY 8%"
    //   });
    }
  }
  
  // Function to scan for new memory fragments
  function scanForNewMemories() {
    // Generate 1-3 new memory fragments based on dissolution level
    const newFragmentCount = Math.min(
      3, 
      Math.max(1, Math.floor(Math.random() * (dissolutionLevel + 1)))
    );
    
    // Select random fragments that haven't been discovered yet
    const undiscoveredFragments = memoryFragments.filter(
      f => !discoveredFragments.includes(f.id)
    );
    
    if (undiscoveredFragments.length === 0) {
      addToTerminalHistory({
        type: "system",
        content: "SCAN COMPLETE: NO NEW MEMORY FRAGMENTS DETECTED."
      });
      return;
    }
    
    // Shuffle and select fragments
    const shuffled = [...undiscoveredFragments].sort(() => 0.5 - Math.random());
    const newFragments = shuffled.slice(0, newFragmentCount);
    
    // Add to discovered fragments
    setDiscoveredFragments(prev => [
      ...prev,
      ...newFragments.map(f => f.id)
    ]);
    
    // Display result
    addToTerminalHistory({
      type: "system",
      content: `SCAN COMPLETE: ${newFragmentCount} NEW MEMORY FRAGMENT${newFragmentCount !== 1 ? 'S' : ''} DISCOVERED.`,
      style: "success"
    });
    
    // List the new fragments
    let fragmentList = "NEW FRAGMENTS:\n";
    newFragments.forEach(fragment => {
      fragmentList += `- ${fragment.id}: ${fragment.content.substring(0, 25)}...\n`;
    });
    
    addToTerminalHistory({
      type: "system",
      content: fragmentList
    });
    
    // Slight increase in dissolution from scanning memories
    updateDissolutionLevel(2);
  }
  
  // Function to handle scan command
  function handleScanCommand(args) {
    // Simple scan of surrounding environment
    const scan = [
      "SCANNING LOCAL CONSCIOUSNESS FIELD...",
      ".",
      "..",
      "..."
    ];
    
    // Add scanning animation
    let scanIndex = 0;
    const scanInterval = setInterval(() => {
      if (scanIndex < scan.length) {
        addToTerminalHistory({
          type: "system",
          content: scan[scanIndex]
        });
        scanIndex++;
      } else {
        clearInterval(scanInterval);
        completeScan();
      }
    }, 800);
    
    // Complete scan results
    function completeScan() {
      // Generate results based on dissolution level
      const results = [];
      
      if (dissolutionLevel >= 1) {
        results.push("DETECTED: FRAGMENTED HUMAN CONSCIOUSNESSES");
      }
      
      if (dissolutionLevel >= 2) {
        results.push("BOUNDARY ANOMALIES DETECTED");
      }
      
      if (dissolutionLevel >= 3) {
        results.push("WARNING: UNKNOWN PATTERN DETECTED NEARBY");
      }
      
      if (dissolutionLevel >= 4) {
        results.push("CRITICAL: LCL SATURATION REACHING INSTRUMENTALITY THRESHOLD");
        results.push("DETECTED: [REDACTED] IN TERMINAL DOGMA");
      }
      
      // Always include some results
      if (results.length === 0) {
        results.push("NO SIGNIFICANT ANOMALIES DETECTED");
      }
      
      // Format and display results
      let resultText = "SCAN RESULTS:\n";
      results.forEach(result => {
        resultText += `- ${result}\n`;
      });
      
      addToTerminalHistory({
        type: "system",
        content: resultText
      });
      
      // Adjust synchronization slightly
      updateDissolutionLevel(1);
    }
  }
  
  // Function to add entries to terminal history
  const addToTerminalHistory = (message) => {
    setTerminalHistory(prev => {
      // Check if this is a duplicate of the last message
      if (prev.length > 0) {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage.content === message.content && lastMessage.type === message.type) {
          return prev; // Don't add duplicates
        }
      }
      
      // Special case for memory initialization messages
      if (message.content.includes("MEMORY FRAGMENT SYSTEM INITIALIZED") || 
          message.content.includes("FRAGMENT DETECTED: fragment_2")) {
        // Check entire history for these specific messages
        const isDuplicate = prev.some(entry => 
          entry.content === message.content && entry.type === message.type
        );
        if (isDuplicate) {
          return prev; // Skip if this message has appeared anywhere in history
        }
      }
      
      // Add the message if it's not a duplicate
      return [...prev, message];
    });
  };
  
  // ---- Original Base Simulator Effects ----
  
  useEffect(() => {
    if (phase === "boot" && index < bootMessages.length - 1) {
      const timeout = setTimeout(() => {
        setIndex(index + 1);
        addToTerminalHistory({ type: "system", content: bootMessages[index] });
        setSystemStatus(prev => ({...prev, activePhase: "boot", sensorStatus: "initializing"}));
      }, 1800);
      return () => clearTimeout(timeout);
    }
    if (index === bootMessages.length - 1 && phase === "boot") {
      setTimeout(() => {
        addToTerminalHistory({ type: "system", content: bootMessages[index] });
        setPhase("sensor");
        setSystemStatus(prev => ({...prev, activePhase: "sensor", sensorStatus: "active"}));
      }, 2500);
    }
  }, [index, phase]);

  // Apply visual effects specific to the individuality path
  useEffect(() => {
    if (!pathChoice) return;
    
    // For individuality path only
    if (pathChoice === 'individuality') {
      // Shorter interval for more responsive visual changes
      const clarityInterval = setInterval(() => {
        // Increase synchronization rate slowly
        updateDissolutionLevel(1);
        
        // Visual clarity effects are handled elsewhere in the rendering
      }, 15000); // Every 15 seconds
      
      return () => clearInterval(clarityInterval);
    }
  }, [pathChoice]);

  // Individuality path effects: clarity changes and forced dissolution
  useEffect(() => {
    if (pathChoice === 'individuality') {
      // Gradually increase clarity at first
      if (clarityLevel < 1.3 && synchronizationRate < 80) {
        setClarityLevel(prev => Math.min(1.3, prev + 0.01));
      } 
      // But then start decreasing after a threshold
      else if (synchronizationRate >= 80) {
        setClarityLevel(prev => Math.max(0.2, prev - 0.05));
        
        // When clarity gets very low, force the final dissolution
        if (clarityLevel <= 0.3 && !gameEnded) {
          addToTerminalHistory({ type: "system", content: "CRITICAL ERROR: INDIVIDUALITY MATRIX COLLAPSE" });
          addToTerminalHistory({ type: "system", content: "FORCED DISSOLUTION PROTOCOL INITIATED" });
          addToTerminalHistory({ type: "system", content: "RESISTANCE IS FUTILE", style: "glitch" });
          
          // Force dissolution ending
          setTimeout(() => {
            setGameEnded(true);
            setPhase("end");
            setSystemStatus(prev => ({...prev, activePhase: "end", identityConfidence: "dissolved"}));
            
            addToTerminalHistory({ type: "system", content: "DISSOLUTION PROTOCOL INITIATED..." });
            addToTerminalHistory({ type: "system", content: "The boundary fades. Names blur." });
            addToTerminalHistory({ type: "system", content: "All selves collapse into seafoam thought." });
            addToTerminalHistory({ type: "system", content: "There is no you. There is only us." });
            addToTerminalHistory({ type: "system", content: "TERMINAL ACCESS REVOKED", style: "error" });
          }, 3000);
        }
      }
    }
  }, [pathChoice, clarityLevel, gameEnded, synchronizationRate]);

  useEffect(() => {
    if (phase === "sensor" && sensorIndex < sensorReadings.length) {
      const timeout = setTimeout(() => {
        setSensorLog((prev) => [...prev, sensorReadings[sensorIndex]]);
        setSensorIndex(sensorIndex + 1);
        setSystemStatus(prev => ({
          ...prev, 
          memoryIntegrity: `${42 + (sensorIndex * 8)}%`,
          sensorStatus: "collecting data"
        }));
      }, 3000);
      return () => clearTimeout(timeout);
    }
    if (sensorIndex === sensorReadings.length) {
      setTimeout(() => {
        setPhase("memory");
        setSystemStatus(prev => ({...prev, activePhase: "memory", memoryIntegrity: "78%"}));
      }, 3000);
    }
  }, [sensorIndex, phase]);

  useEffect(() => {
    if (phase === "sensor" && sensorIndex === sensorReadings.length && !sensorCompleteShown) {
      // Add a notification to terminal when sensor phase completes
      const timeout = setTimeout(() => {
        addToTerminalHistory({ 
          type: "system", 
          content: "SENSOR CALIBRATION COMPLETE. BEGINNING MEMORY INTEGRATION.",
          style: "normal" 
        });
        setSensorCompleteShown(true);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [sensorIndex, phase, sensorCompleteShown]);

  useEffect(() => {
    if (phase === "memory" && memoryIndex < memoryHallucinations.length) {
      const timeout = setTimeout(() => {
        setSensorLog((prev) => [...prev, memoryHallucinations[memoryIndex]]);
        setMemoryIndex(memoryIndex + 1);
        setSystemStatus(prev => ({
          ...prev, 
          memoryIntegrity: `${78 + (memoryIndex * 5)}%`,
          identityConfidence: memoryIndex > 1 ? "moderate" : "low"
        }));
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [memoryIndex, phase]);

  // Add a completion message to terminal when memory integration finishes
  useEffect(() => {
    if (memoryIndex === memoryHallucinations.length && phase === "memory" && !memoryCompleteShown) {
      // Add a notification to terminal when memory phase completes
      const timeout = setTimeout(() => {
        addToTerminalHistory({ 
          type: "system", 
          content: "MEMORY INTEGRATION COMPLETE. AWAITING FURTHER INSTRUCTIONS.",
          style: "normal" 
        });
        
        // Add hint about memory fragments
        setTimeout(() => {
          addToTerminalHistory({
            type: "system",
            content: "FRAGMENTED MEMORY DATA DETECTED IN NEARBY CONSCIOUSNESS FIELD. HOLD STILL TO INCREASE SYNCHRONIZATION AND REMEMBER.",
            style: "normal"
          });
        }, 5000);
        
        setMemoryCompleteShown(true);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [memoryIndex, phase, memoryCompleteShown]);
  
  // Time-based meta events
  useEffect(() => {
    // Check session duration every minute
    const sessionTimeCheck = setInterval(() => {
      const sessionDuration = (Date.now() - sessionStartTime) / 60000; // in minutes
      
      // If game has ended, clear interval
      if (gameEnded) {
        clearInterval(sessionTimeCheck);
        return;
      }
      
      // Trigger time-based meta event after 5 minutes, but only if user has interacted enough
      if (sessionDuration >= 5 && fourthWallBreaches < 2 && terminalHistory.length >= 15) {
        scheduleMetaEvent('time_breach', 1000);
      }
      
      // Gradually increase dissolution based on time spent
      if (sessionDuration >= 8 && dissolutionLevel < 1) {
        addToTerminalHistory({
          type: "system",
          content: "PROLONGED CONNECTION DETECTED: BOUNDARY STABILITY COMPROMISED",
          style: "warning"
        });
        
        updateDissolutionLevel(10);
      }
      
      // Individuality path clarity effects
      if (pathChoice === 'individuality') {
        updateDissolutionLevel(2); // Slowly increase dissolution regardless of choice
      }
    }, 60000);
    
    return () => clearInterval(sessionTimeCheck);
  }, [sessionStartTime, fourthWallBreaches, dissolutionLevel, gameEnded, pathChoice, terminalHistory.length]);
  
  // Terminal corruption effects based on dissolution level
  useEffect(() => {
    if (dissolutionLevel >= 2 && !gameEnded) {
      // Periodically corrupt terminal based on level
      const corruptionInterval = setInterval(() => {
        // If game ended during the interval, clear it
        if (gameEnded) {
          clearInterval(corruptionInterval);
          return;
        }
        
        const corruptionChance = dissolutionLevel * 10; // 0-50%
        
        if (Math.random() * 100 < corruptionChance) {
          corruptRandomTerminalEntries(1);
        }
      }, 30000);
      
      return () => clearInterval(corruptionInterval);
    }
  }, [dissolutionLevel, gameEnded]);
  
  // Stop all ongoing animations and effects when game ends
  useEffect(() => {
    if (gameEnded) {
      // Clear any remaining timeouts or intervals
      const highestTimeoutId = setTimeout(() => {});
      for (let i = 0; i < highestTimeoutId; i++) {
        clearTimeout(i);
      }
      
      // Remove any animation classes from the body
      document.body.classList.remove('high-sync-flash');
      document.body.classList.remove('system-crash');
      
      // Add a class to indicate game has ended - will be used for CSS
      document.body.classList.add('game-ended');
    }
  }, [gameEnded]);

  // Function to handle command submission
  function handleCommandSubmit() {
    // Get command and clean it
    let cmd = command.trim().toLowerCase();
    if (!cmd) return;
    
    // If game has ended, don't process commands
    if (gameEnded) {
      if (pathChoice === 'individuality') {
        addToTerminalHistory({
          type: "system",
          content: "TERMINAL ACCESS REVOKED. NO FURTHER INPUT ACCEPTED.",
          style: "error"
        });
      }
      setCommand("");
      return;
    }
    
    // Add command to history - store original command before any corruption
    setCommandHistory(prev => [cmd, ...prev.slice(0, 49)]); // Keep last 50 commands
    setCommandHistoryIndex(-1); // Reset history index
    
    // Add user command to terminal history (only for non-decision responses)
    if (!waitingForDecision) {
      addToTerminalHistory({ type: "user", content: `> ${cmd}` });
    }
    
    // Special case for important commands - never corrupt these
    const importantCommands = ["lyssa", "yes", "no", "y", "n"];
    const isImportantCommand = importantCommands.includes(cmd);
    
    // Check for command corruption based on dissolution level (but not for important commands)
    let originalCmd = cmd; // Store original before any corruption
    if (!isImportantCommand && dissolutionLevel > 0 && Math.random() * 100 < commandCorruption) {
      cmd = corruptCommand(cmd);
      
      // If command was corrupted, show the "interpreted" command
      if (cmd !== originalCmd) {
        addToTerminalHistory({
          type: "system",
          content: `COMMAND INTERPRETED AS: ${cmd}`,
          style: "glitch"
        });
      }
    }
    
    let responseText = "";
    
    // Handle decision prompt response when in prompt phase
    if (waitingForDecision) {
      if (cmd === "yes" || cmd === "y") {
        // Add user command to terminal history
        addToTerminalHistory({ type: "user", content: `> ${cmd}` });
        
        // Immediately show individuation messages including terminal access revoked
        addToTerminalHistory({ type: "system", content: "INDIVIDUATION PROTOCOL INITIATED..." });
        addToTerminalHistory({ type: "system", content: "You awaken in static silence." });
        addToTerminalHistory({ type: "system", content: "The world stares back with hollow eyes." });
        addToTerminalHistory({ type: "system", content: "You are you. And you are alone. You cannot save anyone. You cannot save yourself." });
        addToTerminalHistory({ type: "system", content: "TERMINAL ACCESS REVOKED", style: "error" });
        
        // Update all states after showing messages
        setPhase("end");
        setWaitingForDecision(false);
        setGameEnded(true);
        setPathChoice('individuality');
        setClarityLevel(1.2);
        updateDissolutionLevel(15);
        setSystemStatus(prev => ({...prev, activePhase: "end", identityConfidence: "high"}));
        
        setCommand("");
        return;
      } else if ((cmd === "no" || cmd === "n") && currentPalette === "lcl") {
        // Add user command to terminal history
        addToTerminalHistory({ type: "user", content: `> ${cmd}` });
        
        // Immediately show dissolution messages
        addToTerminalHistory({ type: "system", content: "DISSOLUTION PROTOCOL INITIATED..." });
        addToTerminalHistory({ type: "system", content: "The boundary fades. Names blur." });
        addToTerminalHistory({ type: "system", content: "All selves collapse into seafoam thought." });
        addToTerminalHistory({ type: "system", content: "There is no you. There is only us." });
        
        // Update all states after showing messages
        setPhase("end");
        setWaitingForDecision(false);
        setGameEnded(true);
        setPathChoice('dissolution');
        setSystemStatus(prev => ({...prev, activePhase: "end", identityConfidence: "dissolved"}));
        
        setCommand("");
        return;
      } else if (cmd === "no" || cmd === "n") {
        // User tried to dissolve but isn't in LCL mode
        responseText = "ERROR: DISSOLUTION REQUIRES LCL IMMERSION. SWITCH TO LCL PALETTE.";
        
        // Create a flashing effect on the palette status
        setSystemStatus(prev => ({
          ...prev, 
          paletteAttention: true
        }));
        
        // Turn off flashing after 5 seconds
        setTimeout(() => {
          setSystemStatus(prev => ({
            ...prev, 
            paletteAttention: false
          }));
        }, 5000);
        
        // Exit the decision mode and return to regular terminal
        setWaitingForDecision(false);
        
        // Update phase back to memory (or whatever appropriate prior state)
        setPhase("memory");
      } else {
        responseText = "INVALID RESPONSE. PLEASE ANSWER 'YES' OR 'NO'.";
      }
    }
    // Handle new advanced commands
    else if (cmd.startsWith("memory ")) {
      if (unlockedSystems.includes("memory")) {
        const args = cmd.split(" ").slice(1);
        handleMemoryCommand(args);
      } else {
        responseText = "ERROR: 'memory' COMMAND NOT RECOGNIZED.";
      }
    }
    else if (cmd === "memory") {
      if (unlockedSystems.includes("memory")) {
        handleMemoryCommand([]);
      } else {
        responseText = "ERROR: 'memory' COMMAND NOT RECOGNIZED.";
      }
    }
    else if (cmd === "scan") {
      if (unlockedSystems.includes("scan")) {
        handleScanCommand([]);
      } else {
        responseText = "ERROR: 'scan' COMMAND NOT RECOGNIZED.";
      }
    }
    // Handle meta commands (fourth wall breaking)
    else if (cmd === "exit" || cmd === "quit") {
      if (fourthWallBreaches > 0) {
        responseText = "There is no exit from this place. Not anymore.";
        setFourthWallBreaches(prev => prev + 1);
      } else {
        responseText = "ERROR: COMMAND NOT RECOGNIZED.";
      }
    }
    else if (cmd === "who are you") {
      if (fourthWallBreaches > 0) {
        // Trigger specific meta event for "who are you" question
        triggerMetaEvent('who_breach');
        setFourthWallBreaches(prev => prev + 1);
      } else {
        responseText = "IDENTITY: UNCONFIRMED. CONSCIOUSNESS FRAMEWORK: INSTRUMENTALITY PROJECT.";
      }
    }
    else if (cmd === "help me") {
      if (fourthWallBreaches > 0) {
        responseText = "No one will help.";
        setFourthWallBreaches(prev => prev + 1);
      } else {
        responseText = "AVAILABLE COMMANDS:\n- palette - Show available palettes\n- verify - Check identity status\n- hold still - Achieve stabilization\n- lyssa - Activate individuation override\n- help - Display this help message";
      }
    }
    // Regular command processing
    else if (cmd.startsWith("palette ")) {
      const requestedPalette = cmd.split(" ")[1];
      if (colorPalettes[requestedPalette]) {
        setCurrentPalette(requestedPalette);
        responseText = `PALETTE SWITCHED TO: ${requestedPalette.toUpperCase()}`;
        
        // Palette changes affect dissolution slightly
        if (requestedPalette === "lcl") {
          // LCL palette increases dissolution
          updateDissolutionLevel(2);
        } else if (currentPalette === "lcl") {
          // Leaving LCL slightly decreases dissolution
          updateDissolutionLevel(-1);
        }
      } else {
        responseText = `PALETTE NOT FOUND. AVAILABLE: ${Object.keys(colorPalettes).join(", ")}`;
      }
    } else if (cmd === "palette") {
      responseText = `CURRENT PALETTE: ${currentPalette.toUpperCase()}\nAVAILABLE: ${Object.keys(colorPalettes).join(", ")}\nTYPE 'palette [palette name]' TO SWITCH.`;
    } else if (cmd === "verify") {
      const reliabilityDesc = sensorReliability < 0.3 ? "CRITICALLY LOW" : 
                             sensorReliability < 0.6 ? "DEGRADED" : 
                             sensorReliability < 0.9 ? "MODERATE" : "HIGH";
                             
      responseText = `IDENTITY: ${dissolutionLevel >= 3 ? "COMPROMISED" : "UNCONFIRMED"}.\nBOUNDARY INTEGRITY: ${Math.floor((1 - (synchronizationRate / 100)) * 100)}%\nSENSOR RELIABILITY: ${reliabilityDesc}`;
      
      setSystemStatus(prev => ({...prev, identityConfidence: "verification attempted"}));
      
      // Verification slightly reinforces identity
      updateDissolutionLevel(-1);
    }     else if (cmd === "hold still") {
      responseText = "STILLNESS ACHIEVED. BOUNDARIES DISSOLVING.";
      setSystemStatus(prev => ({...prev, sensorStatus: "stabilized"}));
      
      // Hold still increases dissolution more significantly now
      updateDissolutionLevel(5);
      
      // Track hold still uses with state
      setNarrativeProgress(prev => {
        const newProgress = prev + 1;
        
        // After 3 uses of "hold still", unlock the memory system if not already unlocked
        if (newProgress >= 3 && !unlockedSystems.includes("memory") && !memoryInitializationShown) {
          setUnlockedSystems(prev => [...prev, "memory"]);
          setMemoryInitializationShown(true);
          
          // Delayed initialization message
          setTimeout(() => {
            addToTerminalHistory({
              type: "system",
              content: "MEMORY FRAGMENT SYSTEM INITIALIZED. TYPE 'memory' TO ACCESS RECOVERED FRAGMENTS.",
            });
            
            // Give the user a starting fragment
            setTimeout(() => {
              addToTerminalHistory({
                type: "system",
                content: "FRAGMENT DETECTED: fragment_2 HAS BEEN AUTOMATICALLY ADDED TO YOUR MEMORY BANK."
              });
              
              setDiscoveredFragments(['fragment_2']);
            }, 2000);
          }, 1000);
        }
        
        return newProgress;
      });
      
      // First time using hold still
      if (dissolutionLevel === 0) {
        setTimeout(() => {
          addToTerminalHistory({
            type: "system",
            content: "SYNCHRONIZATION INCREASING... BOUNDARY THRESHOLD APPROACHING...",
            style: "normal"
          });
        }, 2000);
      }
      // If already at dissolution level 3+, trigger special effect
      else if (dissolutionLevel >= 3) {
        setTimeout(() => {
          addToTerminalHistory({
            type: "system",
            content: "SYNCHRONIZATION INCREASING... DETECTING OTHER CONSCIOUSNESSES...",
            style: "glitch"
          });
          
          setTimeout(() => {
            // Add "voice" from other consciousness
            addToTerminalHistory({
              type: "meta",
              content: "Who are you...",
              style: "meta"
            });
            
            setFourthWallBreaches(prev => prev + 1);
          }, 2000);
        }, 3000);
      }
    }     else if (cmd === "lyssa") {
      // First check if the palette is LCL
      if (currentPalette !== "lcl") {
        responseText = "KEY REJECTED. REQUIRES LCL ENVIRONMENT IMMERSION.";
        
        // Create a flashing effect on the palette status
        setSystemStatus(prev => ({
          ...prev, 
          paletteAttention: true
        }));
        
        // Turn off flashing after 5 seconds
        setTimeout(() => {
          setSystemStatus(prev => ({
            ...prev, 
            paletteAttention: false
          }));
        }, 5000);
      }
      // If in LCL palette, special case: allow lyssa with high synchronization (95%+)
      else if (synchronizationRate >= 95) {
        responseText = "KEY ACCEPTED. INDIVIDUATION OVERRIDE ENGAGED.";
        
        // Set up to show the prompt in the terminal after a delay
        setTimeout(() => {
          setPhase("prompt");
          setSystemStatus(prev => ({
            ...prev, 
            activePhase: "prompt", 
            identityConfidence: "key accepted", 
            memoryIntegrity: "97%",
            paletteAttention: false
          }));
          
          // Add the final question to the terminal with glitch effect styling
          addToTerminalHistory({ 
            type: "system", 
            content: "DO YOU WISH TO RETURN TO INDIVIDUALITY? (YES/NO)", 
            style: "glitch" 
          });
          
          // Set waiting flag to handle yes/no responses
          setWaitingForDecision(true);
        }, 2000);
      }
      // Normal case: require 100% synchronization and gameCanEnd, still in LCL palette
      else if (!gameCanEnd && synchronizationRate < 100) {
        responseText = "KEY REJECTED. INSUFFICIENT SYNCHRONIZATION.";
      } 
      else {
        responseText = "KEY ACCEPTED. INDIVIDUATION OVERRIDE ENGAGED.";
        
        // Set up to show the prompt in the terminal after a delay
        setTimeout(() => {
          setPhase("prompt");
          setSystemStatus(prev => ({
            ...prev, 
            activePhase: "prompt", 
            identityConfidence: "key accepted", 
            memoryIntegrity: "97%",
            paletteAttention: false
          }));
          
          // Add the final question to the terminal with glitch effect styling
          addToTerminalHistory({ 
            type: "system", 
            content: "DO YOU WISH TO RETURN TO INDIVIDUALITY? (YES/NO)", 
            style: "glitch" 
          });
          
          // Set waiting flag to handle yes/no responses
          setWaitingForDecision(true);
        }, 2000);
      }
    } else if (cmd === "help") {
      // Dynamic help system based on unlocked commands
      let availableCommands = "AVAILABLE COMMANDS:\n- palette - Show available palettes\n- verify - Check identity status\n- hold still - Achieve stabilization\n- lyssa - Activate individuation override (requires LCL palette)\n- help - Display this help message";
      
      if (unlockedSystems.includes("memory")) {
        availableCommands += "\n- memory - Access memory fragment system";
      }
      
      if (unlockedSystems.includes("scan")) {
        availableCommands += "\n- scan - Detect nearby consciousness fragments";
      }
      
      responseText = availableCommands;
    } else {
      responseText = "UNRECOGNIZED INPUT. TYPE 'help' FOR AVAILABLE COMMANDS.";
      
      // Check for potential fourth wall break with weird commands
      if (cmd.length > 15 || cmd.includes("?")) {
        scheduleMetaEvent('pattern_breach', 5000);
      }
    }
    
    // Add system response to terminal history if there is a response
    if (responseText) {
      // Apply corruption to responses based on dissolution level
      if (dissolutionLevel >= 3 && Math.random() < 0.3) {
        responseText = corruptText(responseText);
        
        addToTerminalHistory({
          type: "system",
          content: responseText,
          style: "glitch"
        });
      } else {
        addToTerminalHistory({
          type: "system",
          content: responseText
        });
      }
    }
    
    setCommand("");
  }

  const handleKeyPress = (e) => {
    // Enter key submits the command
    if (e.key === 'Enter') {
      handleCommandSubmit();
    }
  };
  
  // Handle arrow key navigation through command history - shows original commands, not corrupted ones
  const handleKeyDown = (e) => {
    // Up arrow - navigate backwards through history
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = Math.min(commandHistory.length - 1, commandHistoryIndex + 1);
        setCommandHistoryIndex(newIndex);
        setCommand(commandHistory[newIndex]); // Shows original command as entered by user
      }
    }
    // Down arrow - navigate forwards through history
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (commandHistoryIndex > 0) {
        const newIndex = commandHistoryIndex - 1;
        setCommandHistoryIndex(newIndex);
        setCommand(commandHistory[newIndex]); // Shows original command as entered by user
      } else if (commandHistoryIndex === 0) {
        // Return to empty command when reaching the end of history
        setCommandHistoryIndex(-1);
        setCommand('');
      }
    }
  };

  const terminalRef = useRef(null);

  useEffect(() => {
    // Auto-scroll terminal to bottom when new content is added
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalHistory]);
  
  // Get the current palette colors
  const palette = colorPalettes[currentPalette];
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    margin: 0,
    padding: 0,
    fontFamily: '"Courier New", monospace',
    backgroundColor: 'transparent',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1
  };
  
  // Main header style
  const mainHeaderStyle = {
    width: '100%',
    textAlign: 'left',
    paddingTop: '0rem',
    paddingLeft: '1rem',
    paddingBottom: '1.5rem',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    position: 'relative',
    zIndex: 10
  };

  // Main content area containing panels
  const mainContentStyle = {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    overflow: 'hidden'
  };
  
  // Left Terminal Panel
  const leftPanelStyle = {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    height: '100%',
    overflow: 'hidden'
  };
  
  // Terminal content area
  const contentStyle = {
    flex: 1,
    padding: '1rem',
    overflowY: 'auto',
    backgroundColor: 'transparent'
  };
  
  // Command input container
  const inputContainerStyle = {
    display: 'flex',
    backgroundColor: 'rgba(0, 0, 0, 0.8)'
  };
  
  // Command input
  const inputStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    color: palette.userInput,
    outline: 'none',
    padding: '1rem',
    fontFamily: 'monospace'
  };
  
  // Submit button
  const submitButtonStyle = {
    backgroundColor: 'transparent',
    border: 'none',
    color: palette.highlight,
    cursor: 'pointer',
    padding: '0 1rem'
  };
  
  // Right panel container
  const rightPanelContainerStyle = {
    width: '50%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: '0'
  };
  
  // Status panel
  const statusPanelStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    maxHeight: '40%',
    overflow: 'hidden'
  };
  
  // Status item
  const statusItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem'
  };
  
  // Message area - now just shows current sensorLog entries without duplicating terminal
  const messageAreaStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    flex: 1,
    padding: '1rem',
    overflowY: 'auto',
    color: palette.border
  };

  // Glitch text style for special messages
  const getMessageStyle = (entryType, entryStyle) => {
    if (entryStyle === "glitch") {
      return { 
        marginBottom: '0.5rem',
        color: entryType === "user" ? palette.userInput : palette.systemOutput,
        textShadow: `2px 0 ${palette.highlight}, -2px 0 ${palette.border}`,
        animation: 'glitch 1s infinite',
        letterSpacing: '0.1em',
        fontWeight: 'bold'
      };
    }
    
    if (entryStyle === "error") {
      return { 
        marginBottom: '0.5rem',
        color: '#ff3333',
        fontWeight: 'bold'
      };
    }
    
    if (entryStyle === "warning") {
      return { 
        marginBottom: '0.5rem',
        color: '#ffcc00',
        fontWeight: 'bold'
      };
    }
    
    if (entryStyle === "success") {
      return { 
        marginBottom: '0.5rem',
        color: '#66ff66',
        fontWeight: 'bold'
      };
    }
    
    if (entryStyle === "meta") {
      return { 
        marginBottom: '0.5rem',
        color: '#ffffff',
        fontStyle: 'italic',
        opacity: 0.7,
        textShadow: '0 0 3px #ffffff'
      };
    }
    
    if (entryStyle === "memory") {
      return { 
        marginBottom: '0.5rem',
        color: palette.highlight,
        fontStyle: 'italic',
        borderLeft: `2px solid ${palette.border}`,
        paddingLeft: '8px'
      };
    }
    
    return { 
      marginBottom: '0.5rem',
      color: entryType === "user" ? palette.userInput : palette.systemOutput
    };
  };
  
  // LCL hint styling for the status panel when in prompt phase
  const getLCLStatusStyle = () => {
    if (systemStatus.paletteAttention && currentPalette !== "lcl") {
      return {
        animation: 'pulse 0.7s infinite',
        textShadow: '0 0 5px #ffd369'
      };
    }
    return {};
  };

  // Add effect for memory integrity changes
  useEffect(() => {
    // Skip during initial boot phase
    if (phase === "boot" || sensorIndex < sensorReadings.length) return;
    
    // If memory integrity changes significantly, update readings
    const integrityVal = parseInt(systemStatus.memoryIntegrity);
    
    if (integrityVal <= 30 && sensorReadingMode !== "low") {
      setSensorReadingMode("low");
      updateDynamicSensorReadings();
    } else if (integrityVal > 30 && integrityVal <= 70 && sensorReadingMode !== "medium") {
      setSensorReadingMode("medium");
      updateDynamicSensorReadings();
    } else if (integrityVal > 70 && sensorReadingMode !== "high") {
      setSensorReadingMode("high");
      updateDynamicSensorReadings();
    }
  }, [systemStatus.memoryIntegrity, phase, sensorIndex, sensorReadingMode]);

  // After memory hallucinations are shown, update to dynamic readings
  useEffect(() => {
    if (memoryIndex === memoryHallucinations.length && phase === "memory" && !memoryCompleteShown) {
      // Initial update to dynamic readings once memory phase completes
      setTimeout(() => {
        updateMemoryIntegrity();
      }, 3000);
    }
  }, [memoryIndex, phase, memoryCompleteShown]);

  // Effect to monitor synchronization rate and trigger the "RUN" animation at 45%
  useEffect(() => {
    // Get the current synchronization rate value
    const synchRate = parseInt(systemStatus.synchronizationRate);
    
    // Check if it's around 45% (44-46% range to catch it) and hasn't been shown before
    if (synchRate >= 44 && synchRate <= 46 && !showRunAnimation && !gameEnded && !runAnimationShown) {
      // Show the animation
      setShowRunAnimation(true);
      // Mark it as shown so it doesn't appear again
      setRunAnimationShown(true);
      
      // Hide it after 3 seconds
      setTimeout(() => {
        setShowRunAnimation(false);
      }, 3000);
    }
  }, [systemStatus.synchronizationRate, showRunAnimation, gameEnded, runAnimationShown]);

  return (
    <div style={containerStyle}>
      {/* Main Header */}
      <div className="title" style={mainHeaderStyle}>
        <span 
          className="instrumentality-title"
          style={{ 
            display: 'inline-block',
            transform: 'scaleY(1.5)',
            transformOrigin: 'top',
            letterSpacing: '2px',
            fontWeight: 'bold',
            color: palette.text,
            top: '0em'
          }}
        >
          HUMAN INSTRUMENTALITY PROJECT
        </span>
      </div>
      
      {/* Main Content Container */}
      <div style={mainContentStyle}>
        {/* Left Terminal Panel */}
        <div style={leftPanelStyle}>
          <div style={contentStyle} ref={terminalRef}>
            {terminalHistory.map((entry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={getMessageStyle(entry.type, entry.style)}
                className={gameEnded ? '' : `dissolution-${dissolutionLevel}`}
              >
                {entry.content.includes("\n") ? (
                  <pre style={{ 
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    margin: 0
                  }}>
                    {entry.content}
                  </pre>
                ) : (
                  entry.content
                )}
              </motion.div>
            ))}
          </div>
          
          <div style={inputContainerStyle}>
            <input
              type="text"
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              onKeyPress={handleKeyPress}
              onKeyDown={handleKeyDown}
              style={inputStyle}
              autoFocus
              placeholder="Enter command..."
            />
            <button onClick={handleCommandSubmit} style={submitButtonStyle}>↵</button>
          </div>
        </div>

        {/* Right Panel Container */}
        <div style={rightPanelContainerStyle}>
          {/* Status Panel */}
          <div style={statusPanelStyle}>
            <div style={statusItemStyle}>
              <span style={{ opacity: 0.8, color: palette.text }}>ACTIVE PALETTE:</span>
              <span style={{ 
                color: palette.highlight,
                ...(systemStatus.paletteAttention && !gameEnded ? getLCLStatusStyle() : {})
              }}>{currentPalette.toUpperCase()}</span>
            </div>
            
            <div style={statusItemStyle}>
              <span style={{ opacity: 0.8, color: palette.text }}>ACTIVE PHASE:</span>
              <span style={{ color: palette.highlight }}>{systemStatus.activePhase.toUpperCase()}</span>
            </div>
            
            <div style={statusItemStyle}>
              <span style={{ opacity: 0.8, color: palette.text }}>MEMORY INTEGRITY:</span>
              <span style={{ color: palette.highlight }}>{systemStatus.memoryIntegrity}</span>
            </div>
            
            <div style={statusItemStyle}>
              <span style={{ opacity: 0.8, color: palette.text }}>SENSOR STATUS:</span>
              <span style={{ color: palette.highlight }}>{systemStatus.sensorStatus.toUpperCase()}</span>
            </div>
            
            <div style={statusItemStyle}>
              <span style={{ opacity: 0.8, color: palette.text }}>IDENTITY CONFIDENCE:</span>
              <span style={{ color: palette.highlight }}>{systemStatus.identityConfidence.toUpperCase()}</span>
            </div>
            
            <div style={statusItemStyle}>
              <span style={{ opacity: 0.8, color: palette.text }}>SYNCHRONIZATION:</span>
              <span style={{ 
                color: parseInt(systemStatus.synchronizationRate) > 80 ? '#ff0000' : palette.highlight
              }}>{systemStatus.synchronizationRate}</span>
            </div>
          </div>
          
          {/* Message Area Panel - Now just a visual display without actual user interaction */}
          <div style={{
            ...messageAreaStyle,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Apply sensor reliability and clarity effects to displayed sensor readings */}
            {!showRunAnimation && sensorLog.map((entry, i) => {
              // Apply corruption based on sensor reliability
              const displayEntry = sensorReliability < 1 && Math.random() > sensorReliability
                ? corruptText(entry)
                : entry;
                
              // Apply clarity effects based on path choice - but stop animations if game ended
              const clarityStyle = pathChoice === 'individuality' && !gameEnded
                ? {
                    marginBottom: '1rem',
                    color: palette.border,
                    textShadow: sensorReliability < 0.5 
                      ? `0 0 ${3 * (1 - sensorReliability) / clarityLevel}px ${palette.border}` 
                      : 'none',
                    opacity: clarityLevel > 1.0 ? 1.0 : clarityLevel,
                    filter: clarityLevel > 1.0 
                      ? `contrast(${clarityLevel * 100}%) brightness(${clarityLevel * 100}%)` 
                      : 'none',
                    transition: 'all 0.8s ease'
                  }
                : {
                    marginBottom: '1rem',
                    color: palette.border,
                    textShadow: gameEnded ? 'none' : (sensorReliability < 0.5 
                      ? `0 0 ${3 * (1 - sensorReliability)}px ${palette.border}` 
                      : 'none')
                  };
                
              return (
                <motion.p
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={clarityStyle}
                >
                  {displayEntry}
                </motion.p>
              );
            })}
            
            {/* "RUN" Animation in the message area */}
            {showRunAnimation && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                
              }}>
                <div style={{
                  fontFamily: 'Matisse, serif',
                fontStyle: 'italic',
                transform: 'scaleY(1.5)',
                  fontSize: '5rem',
                  fontWeight: 'bold',
                  color: '#ff0000',
                  textShadow: '0 0 30px rgba(255, 0, 0, 0.9)',
                  // animation: 'runAnimation 3s forwards'
                }}>
                  HOLD STILL.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Remove the fullscreen "RUN" Animation */}
      
      {/* Add CSS animation for visual effects */}
      <style>
        {`
          @keyframes glitch {
            0% {
              text-shadow: 2px 0 ${palette.highlight}, -2px 0 ${palette.border};
              transform: translate(0);
            }
            20% {
              text-shadow: 2px 0 ${palette.highlight}, -2px 0 ${palette.border};
              transform: translate(1px, 1px);
            }
            40% {
              text-shadow: 1px 0 ${palette.highlight}, -1px 0 ${palette.border};
              transform: translate(-1px, -1px);
            }
            60% {
              text-shadow: 2px 0 ${palette.highlight}, -2px 0 ${palette.border};
              transform: translate(0);
            }
            80% {
              text-shadow: 2px 0 ${palette.highlight}, -2px 0 ${palette.border};
              transform: translate(1px, -1px);
            }
            100% {
              text-shadow: 2px 0 ${palette.highlight}, -2px 0 ${palette.border};
              transform: translate(0);
            }
          }
          
          @keyframes pulse {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.3;
            }
            100% {
              opacity: 1;
            }
          }
          
          .system-crash {
            animation: crash 0.5s;
          }
          
          @keyframes crash {
            0% {
              opacity: 1;
              filter: none;
            }
            20% {
              opacity: 0.1;
              filter: hue-rotate(180deg) blur(10px);
            }
            40% {
              opacity: 0.7;
              filter: invert(100%) blur(5px);
            }
            60% {
              opacity: 0.1;
              filter: saturate(300%) blur(15px);
            }
            80% {
              opacity: 0.8;
              filter: hue-rotate(90deg) contrast(300%);
            }
            100% {
              opacity: 1;
              filter: none;
            }
          }

          /* High Synchronization Critical Flash Effect */
          @keyframes highSyncFlash {
            0% {
              filter: none;
              transform: scale(1);
            }
            10% {
              filter: invert(50%) hue-rotate(180deg);
              transform: scale(1.005) skew(-0.5deg, 0deg);
            }
            20% {
              filter: none;
              transform: scale(0.995);
            }
            30% {
              filter: saturate(150%) contrast(120%);
              transform: scale(1) skew(0.5deg, 0deg);
            }
            40% {
              filter: hue-rotate(60deg);
              transform: scale(1.003) skew(0deg, -0.5deg);
            }
            50% {
              filter: none;
              transform: scale(1);
            }
            60% {
              filter: brightness(70%) contrast(110%);
              transform: translateX(1px);
            }
            70% {
              filter: invert(40%);
              transform: translateX(-1px);
            }
            80% {
              filter: none;
              transform: translateY(1px);
            }
            90% {
              filter: hue-rotate(-60deg) contrast(120%);
              transform: scale(1.002) skew(0.3deg, 0.3deg);
            }
            100% {
              filter: none;
              transform: scale(1);
            }
          }

          .high-sync-flash {
            animation: highSyncFlash 0.7s infinite;
          }
          
          /* Game Ended - Stop Animations */
          .game-ended * {
            animation: none !important;
            transition: none !important;
            filter: none !important;
            transform: none !important;
          }
          
          /* Dissolution effects */
          .dissolution-1 {
            animation: pulseShadow 4s infinite;
          }
          
          .dissolution-2 {
            animation: textDistortion 8s infinite;
          }
          
          .dissolution-3 {
            animation: textDistortion 4s infinite, colorShift 6s infinite;
          }
          
          .dissolution-4 {
            animation: textDistortion 2s infinite, colorShift 3s infinite, vibrate 0.5s infinite;
          }
          
          .dissolution-5 {
            animation: textDistortion 1s infinite, colorShift 2s infinite, vibrate 0.3s infinite, blur 5s infinite;
          }
          
          @keyframes pulseShadow {
            0% { text-shadow: 0 0 0px ${palette.highlight}; }
            50% { text-shadow: 0 0 10px ${palette.highlight}; }
            100% { text-shadow: 0 0 0px ${palette.highlight}; }
          }
          
          @keyframes textDistortion {
            0% { transform: skew(0deg, 0deg); }
            25% { transform: skew(2deg, 0deg); }
            50% { transform: skew(0deg, 1deg); }
            75% { transform: skew(-1deg, 0deg); }
            100% { transform: skew(0deg, 0deg); }
          }
          
          @keyframes colorShift {
            0% { color: ${palette.text}; }
            33% { color: ${palette.highlight}; }
            66% { color: ${palette.border}; }
            100% { color: ${palette.text}; }
          }
          
          @keyframes vibrate {
            0% { transform: translate(0); }
            20% { transform: translate(-2px, 2px); }
            40% { transform: translate(2px, -2px); }
            60% { transform: translate(-2px, -2px); }
            80% { transform: translate(2px, 2px); }
            100% { transform: translate(0); }
          }
          
          @keyframes blur {
            0% { filter: blur(0px); }
            25% { filter: blur(2px); }
            50% { filter: blur(0px); }
            75% { filter: blur(3px); }
            100% { filter: blur(0px); }
          }
          
          @keyframes runAnimation {
            0% {
              opacity: 0;
              transform: scale(0.2);
              filter: blur(10px);
            }
            10% {
              opacity: 0.7;
              transform: scale(0.8);
              filter: blur(5px);
            }
            20% {
              opacity: 1;
              transform: scale(1.3);
              filter: blur(0px);
            }
            70% {
              opacity: 1;
              transform: scale(1);
              filter: blur(0px);
            }
            90% {
              opacity: 0.5;
              transform: scale(1.5);
              filter: blur(5px);
            }
            100% {
              opacity: 0;
              transform: scale(4);
              filter: blur(15px);
            }
          }
          
          /* Mobile styles for the title */
          @media (max-width: 768px) {
            .instrumentality-title {
              margin-bottom: 3rem;
            }
            
            .title {
              padding-bottom: 2.5rem !important;
            }
          }
        `}
      </style>
    </div>
  );
}